import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import { useTheme } from '../../../theme/hooks'

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const LidarProgressIndicator = ({
  active,
  progressState,
  onClose,
  position,
  setPosition,
  queue,
}) => {
  const { theme, isDark } = useTheme()
  const [isHovered, setIsHovered] = useState(false)
  const hoverTimerRef = useRef(null)
  const containerRef = useRef(null)
  const dragState = useRef({
    dragging: false,
    offsetX: 0,
    offsetY: 0,
  })
  const isError = progressState.isError

  // Theme-aware colors
  const colors = {
    bg: isDark 
      ? 'rgba(2, 6, 23, 0.9)' // slate-950/90
      : 'rgba(255, 255, 255, 0.95)', // white/95
    border: isDark
      ? 'rgba(255, 255, 255, 0.1)' // white/10
      : 'rgba(0, 0, 0, 0.1)', // black/10
    label: isDark
      ? 'rgba(147, 197, 253, 0.8)' // blue-300/80
      : 'rgba(37, 99, 235, 0.8)', // blue-600/80
    textPrimary: isDark
      ? 'rgba(255, 255, 255, 0.8)' // white/80
      : 'rgba(17, 24, 39, 0.9)', // gray-900/90
    textSecondary: isDark
      ? 'rgba(255, 255, 255, 0.7)' // white/70
      : 'rgba(55, 65, 81, 0.8)', // gray-700/80
    textTertiary: isDark
      ? 'rgba(255, 255, 255, 0.4)' // white/40
      : 'rgba(107, 114, 128, 0.7)', // gray-500/70
    errorText: isDark
      ? 'rgba(254, 202, 202, 1)' // red-200
      : theme.status.error.text, // red-900 from theme
    errorBg: isDark
      ? 'rgba(254, 202, 202, 1)' // red-200
      : theme.status.error.text, // red-900 from theme
    progressBg: isDark
      ? 'rgba(255, 255, 255, 0.1)' // white/10
      : 'rgba(0, 0, 0, 0.05)', // black/5
    logsBg: isDark
      ? 'rgba(15, 23, 42, 0.7)' // slate-900/70
      : 'rgba(249, 250, 251, 0.9)', // gray-50/90
    logsBorder: isDark
      ? 'rgba(255, 255, 255, 0.1)' // white/10
      : 'rgba(0, 0, 0, 0.1)', // black/10
  }

  useEffect(() => {
    const handleMove = event => {
      if (!dragState.current.dragging || !containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const nextX = event.clientX - dragState.current.offsetX
      const nextY = event.clientY - dragState.current.offsetY
      const maxX = window.innerWidth - rect.width
      const maxY = window.innerHeight - rect.height
      setPosition({
        x: clamp(nextX, 12, maxX - 12),
        y: clamp(nextY, 12, maxY - 12),
      })
    }

    const handleUp = () => {
      dragState.current.dragging = false
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
    }

    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    return () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current)
      }
    }
  }, [setPosition])

  const handlePointerDown = event => {
    if (event.target.closest('button')) return
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    dragState.current.dragging = true
    dragState.current.offsetX = event.clientX - rect.left
    dragState.current.offsetY = event.clientY - rect.top
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'grabbing'
  }

  const keepHover = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
    setIsHovered(true)
  }

  const releaseHover = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
    hoverTimerRef.current = setTimeout(() => {
      if (containerRef.current?.matches(':hover')) return
      setIsHovered(false)
    }, 200)
  }

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          ref={containerRef}
          initial={{ opacity: 0, scale: 0.9, y: -8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: -8 }}
          transition={{ duration: 0.22 }}
          className="fixed z-[100000]"
          style={{ left: position.x, top: position.y }}
          onPointerDown={handlePointerDown}
          onMouseEnter={() => {
            if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
            hoverTimerRef.current = setTimeout(() => {
              setIsHovered(true)
            }, 250)
          }}
          onMouseLeave={releaseHover}
        >
          <motion.div
            className="backdrop-blur"
            style={{ 
              borderRadius: 18,
              backgroundColor: colors.bg,
              border: `1px solid ${colors.border}`,
              boxShadow: isDark 
                ? '0 12px 30px rgba(2, 6, 23, 0.45)'
                : '0 12px 30px rgba(0, 0, 0, 0.15)',
            }}
            animate={{
              width: 260,
              paddingTop: 10,
              paddingBottom: isHovered ? 16 : 10,
              paddingLeft: 12,
              paddingRight: 12,
            }}
            transition={{ type: 'spring', stiffness: 260, damping: 24 }}
          >
            <div className="flex items-center gap-3">
              <span 
                className="text-[10px] uppercase tracking-[0.2em]"
                style={{ color: colors.label }}
              >
                LIDAR:
              </span>
              {isError ? (
                <div 
                  className="flex-1 text-[11px] font-semibold"
                  style={{ color: colors.errorText }}
                >
                  Erreur
                </div>
              ) : (
                <>
                  <div className="relative flex-1">
                    <div 
                      className="h-1.5 overflow-hidden rounded-full"
                      style={{ backgroundColor: colors.progressBg }}
                    >
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${Math.min(progressState.percent, 100)}%`,
                          background: isDark
                            ? 'linear-gradient(to right, #3b82f6, #22d3ee, #10b981)' // blue-500, cyan-400, emerald-400
                            : 'linear-gradient(to right, #2563eb, #06b6d4, #059669)', // blue-600, cyan-600, emerald-600
                        }}
                      />
                      <div 
                        className="absolute inset-0 bg-[length:200%_100%] animate-[lidarFlow_1.6s_linear_infinite]"
                        style={{
                          background: `linear-gradient(110deg, transparent, rgba(${isDark ? '255,255,255' : '0,0,0'}, 0.35), transparent)`,
                        }}
                      />
                    </div>
                  </div>
                  <span 
                    className="text-[11px] font-semibold"
                    style={{ color: colors.textPrimary }}
                  >
                    {Math.round(progressState.percent)}%
                  </span>
                </>
              )}
              <button
                onClick={onClose}
                className="rounded-full p-1 transition-colors"
                style={{
                  border: `1px solid ${colors.border}`,
                  color: colors.textSecondary,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                  e.currentTarget.style.color = colors.textPrimary
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = colors.textSecondary
                }}
                title="Masquer"
              >
                <X size={14} />
              </button>
            </div>

            <AnimatePresence>
              {isHovered && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="mt-4 space-y-2"
                >
                  {isError ? (
                    <div 
                      className="truncate whitespace-nowrap text-sm font-semibold"
                      style={{ color: colors.errorText }}
                    >
                      Erreur pendant l&apos;import
                    </div>
                  ) : (
                    <div className="min-w-0">
                      <div 
                        className="truncate text-xs font-semibold"
                        style={{ color: colors.textPrimary }}
                      >
                        {progressState.status || 'Traitement en cours…'}
                      </div>
                    </div>
                  )}

                  {progressState.fileName && (
                    <div className="text-[11px]" style={{ color: colors.textSecondary }}>
                      <span className="mr-2" style={{ color: colors.textTertiary }}>Fichier</span>
                      <span className="whitespace-normal break-words">
                        {progressState.fileName}
                      </span>
                    </div>
                  )}

                  {Array.isArray(queue) && queue.length > 0 && (
                    <div className="mt-2 text-[10px]" style={{ color: colors.textSecondary }}>
                      <div className="mr-2" style={{ color: colors.textTertiary }}>
                        File d&apos;attente :
                      </div>
                      <div className="mt-1 space-y-1">
                        {queue.map((item, index) => (
                          <div
                            key={item.uploadId || `${item.fileName}-${index}`}
                            className="flex items-center justify-between gap-2"
                          >
                            <span className="min-w-0 truncate" style={{ color: colors.textSecondary }}>
                              {item.fileName}
                            </span>
                            <span className="shrink-0" style={{ color: colors.textTertiary }}>
                              {index === 0 ? 'En cours' : 'En attente'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {isHovered && !isError && progressState.logs.length > 0 && (
                <motion.div
                  key="logs-section"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="mt-2 max-h-20 overflow-y-auto rounded-md px-2 py-1 text-[10px] font-mono"
                  style={{
                    backgroundColor: colors.logsBg,
                    border: `1px solid ${colors.logsBorder}`,
                  }}
                  onMouseEnter={keepHover}
                  onMouseLeave={releaseHover}
                  onWheel={keepHover}
                >
                  {progressState.logs
                    .filter(log => log.message !== progressState.status)
                    .slice(-3)
                    .map((log, index, arr) => {
                      const isLatest = index === arr.length - 1
                      const logColor = isLatest
                        ? colors.textPrimary
                        : index === arr.length - 2
                        ? colors.textSecondary
                        : colors.textTertiary
                      return (
                        <div
                          key={log.id || `${log.ts || ''}-${log.message}-${index}`}
                          className="truncate"
                          style={{ color: logColor }}
                        >
                          {log.message}
                        </div>
                      )
                    })}
                </motion.div>
              )}
              {isHovered && isError && progressState.detail && (
                <motion.div
                  key="error-detail-section"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="mt-2 text-[10px]"
                  style={{ color: colors.errorText, opacity: 0.8 }}
                >
                  <span className="truncate">{progressState.detail}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
      <style>{`
        @keyframes lidarFlow {
          0% { background-position: 0% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </AnimatePresence>
  )
}

LidarProgressIndicator.propTypes = {
  active: PropTypes.bool.isRequired,
  progressState: PropTypes.shape({
    percent: PropTypes.number.isRequired,
    status: PropTypes.string,
    isError: PropTypes.bool,
    detail: PropTypes.string,
    fileName: PropTypes.string,
    logs: PropTypes.arrayOf(
      PropTypes.shape({
        message: PropTypes.string,
        id: PropTypes.string,
        ts: PropTypes.number,
      }),
    ),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  position: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }).isRequired,
  setPosition: PropTypes.func.isRequired,
  queue: PropTypes.arrayOf(
    PropTypes.shape({
      fileName: PropTypes.string,
      uploadId: PropTypes.string,
    }),
  ),
}

export default LidarProgressIndicator
